import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import FormatListNumberedSharpIcon from '@mui/icons-material/FormatListNumberedSharp';
import { useMediaQuery } from "@mui/material";
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import { useTheme } from "@mui/material/styles";
import React from "react";
import Clock from "../clock/clock";
import { MEDIA_QUERIES } from "../responsive/mediaQueries";
import "./header.css";
import { iconStyle } from "./headerConstants";


const Header = ({ header, onClick, checked = [false, false], handleCheck, filterStore, userStore, currentView, precision }) => {
    const theme = useTheme();
    const mode = theme.palette.mode;

    // Use the same media queries as in ItemContent
    const isXSmallScreen = useMediaQuery(MEDIA_QUERIES.xs);
    const isSmallScreen = useMediaQuery(MEDIA_QUERIES.sm);
    const isMediumScreen = useMediaQuery(MEDIA_QUERIES.md);

    // Function to determine if a column should be visible based on screen size
    const shouldShowColumn = (columnField) => {
        // Priority columns that should always be visible
        const alwaysVisibleColumns = ['', 'order', 'play', 'sentence', 'speaker', 'sentiment'];

        // Secondary columns that should be visible on medium screens and larger
        const mediumScreenColumns = [...alwaysVisibleColumns, 'length', 'sec', 'question'];

        // Columns visible only on large screens
        const smallScreenColumns = [...alwaysVisibleColumns, 'length'];

        if (isXSmallScreen) {
            return alwaysVisibleColumns.includes(columnField);
        } else if (isSmallScreen) {
            return smallScreenColumns.includes(columnField);
        } else if (isMediumScreen) {
            return mediumScreenColumns.includes(columnField);
        }

        // On large screens, respect the visibility setting from the header
        return true;
    };

    // Styling for header cells based on screen size
    const getResponsiveHeaderStyle = (obj) => {
        let width = obj.width;

        // Use dynamic width for video columns if filterStore and currentView are available
        if (obj.type === 'video' && filterStore && currentView) {
            const dynamicVideoWidth = filterStore.getVideoWidth(currentView);
            if (isXSmallScreen) {
                width = '80px';
            } else if (isSmallScreen) {
                width = '100px';
            } else if (isMediumScreen) {
                width = '150px';
            } else {
                width = `${dynamicVideoWidth}px`;
            }
        }

        // Use dynamic width for sentence columns to match body
        if (obj.field === 'sentence' && filterStore && currentView) {
            const dynamicVideoWidth = filterStore.getVideoWidth(currentView);

            if (isXSmallScreen) {
                width = '100%';
            } else if (isSmallScreen) {
                width = dynamicVideoWidth <= 50 ? '200px' : '150px';
            } else if (isMediumScreen) {
                width = dynamicVideoWidth <= 50 ? '350px' :
                    dynamicVideoWidth <= 100 ? '300px' : '250px';
            } else {
                // Same calculation as in ItemContent:
                // 30px video → 300px sentence
                // 90px video → 280px sentence  
                // 150px video → 250px sentence
                let sentenceWidth;
                if (dynamicVideoWidth <= 30) {
                    sentenceWidth = 300;
                } else if (dynamicVideoWidth <= 90) {
                    // Linear interpolation between 30px→300px and 90px→280px
                    const ratio = (dynamicVideoWidth - 30) / (90 - 30);
                    sentenceWidth = 300 + (280 - 300) * ratio;
                } else {
                    // Linear interpolation between 90px→280px and 150px→250px
                    const ratio = (dynamicVideoWidth - 90) / (150 - 90);
                    sentenceWidth = 280 + (250 - 280) * ratio;
                }
                width = `${Math.round(sentenceWidth)}px`;
            }
        }

        const baseStyle = {
            width: width,
            textAlign: obj.align || 'center',
            verticalAlign: 'middle',
            fontSize: isXSmallScreen ? '12px' : isSmallScreen ? '13px' : '14px',
            padding: isXSmallScreen ? '2px' : isSmallScreen ? '3px' : '4px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        };

        // Special override for Clock component column
        if (obj.type === 'icon' && obj.field === 'duration') {
            return {
                ...baseStyle,
                padding: 0, // Remove padding from TH
                textAlign: 'center', // Center align TH content
                width: '80px', // Fixed width to prevent resizing when precision changes
                minWidth: '80px', // Ensure minimum width
                maxWidth: '80px', // Prevent expansion
            };
        }
        return baseStyle;
    };

    // maybe a string or a component    
    const SortArrow = ({ index }) =>
    (header[index].isUp ? <ArrowDropDownIcon color="action" sx={{ fontSize: isSmallScreen ? 16 : 20 }} /> :
        <ArrowDropUpIcon color="action" sx={{ fontSize: isSmallScreen ? 16 : 20 }} />);

    return (
        <tr
            className="responsive-header"
            style={{
                width: '100%',
                top: 0,
                backgroundColor: mode === 'dark' ? 'black' : 'white',
                height: isXSmallScreen ? '28px' : isSmallScreen ? '32px' : '60px',
            }}
        >
            {header.map((obj, index) => {
                // Only show columns that are visible and should be shown on current screen size
                if (obj.visible && shouldShowColumn(obj.field)) {
                    if (obj.restore)
                        return (
                            <th key={index} style={getResponsiveHeaderStyle(obj)}>
                                <IconButton
                                    onClick={() => onClick(index)}
                                    style={{
                                        padding: isXSmallScreen ? '2px' : '4px',
                                        marginRight: isXSmallScreen ? '0' : '2px'
                                    }}
                                >
                                    <FormatListNumberedSharpIcon sx={{
                                        ...iconStyle,
                                        fontSize: isXSmallScreen ? 12 : isSmallScreen ? 14 : 15
                                    }} />
                                </IconButton>
                            </th>
                        )
                    if (obj.type === 'checkbox')
                        return (
                            <th key={index} style={getResponsiveHeaderStyle(obj)}>
                                <Checkbox
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            fontSize: isXSmallScreen ? 12 : isSmallScreen ? 14 : 15
                                        },
                                        padding: isXSmallScreen ? '2px' : '4px'
                                    }}
                                    checked={checked[0]}
                                    indeterminate={checked[1]}
                                    onChange={handleCheck}
                                />
                            </th>
                        )
                    if (obj.field === 'duration') {
                        return (
                            <th key={index} style={getResponsiveHeaderStyle(obj)}>
                                <Clock header={true} duration={[userStore.getTotalDuration(), userStore.getDuration()]} />
                            </th>
                        )
                    }
                    // Determine if this is a long title that needs special handling
                    const isLongTitle = typeof obj.title === 'string' && obj.title.length > 5;

                    return (
                        <th
                            key={index}
                            style={getResponsiveHeaderStyle(obj)}
                            className={isLongTitle ? 'long-title' : ''}
                        >
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: obj.align === 'right' ? 'flex-end' : obj.align === 'center' ? 'center' : 'flex-start',
                                justifyContent: 'center',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: obj.align === 'right' ? 'flex-end' : obj.align === 'center' ? 'center' : 'flex-start',
                                    justifyContent: obj.align === 'right' ? 'flex-end' : obj.align === 'center' ? 'center' : 'flex-start',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {obj.sort &&
                                        <IconButton
                                            onClick={() => onClick(index, (header[index].isUp ? 'down' : 'up'))}
                                            sx={{
                                                padding: 0,
                                                ml: isXSmallScreen ? 0 : 0.2,
                                                fontSize: isXSmallScreen ? 12 : isSmallScreen ? 14 : 18
                                            }}
                                        >
                                            {typeof obj.title === 'string' && isXSmallScreen
                                                ? obj.title.substring(0, 3) // Show abbreviated text on extremely small screens
                                                : obj.title
                                            }
                                            <SortArrow index={index} />
                                        </IconButton>
                                    }
                                    {!obj.sort && (
                                        typeof obj.title === 'string' && isXSmallScreen
                                            ? obj.title.substring(0, 3) // Show abbreviated text on extremely small screens
                                            : obj.title
                                    )}
                                </Box>
                                {obj.subtitle && (
                                    <Box sx={{
                                        fontSize: isXSmallScreen ? '8px' : isSmallScreen ? '9px' : '10px',
                                        color: 'text.secondary',
                                        marginTop: '2px',
                                        lineHeight: 1,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                        textAlign: obj.align || 'left'
                                    }}>
                                        {typeof obj.subtitle === 'string' && isXSmallScreen
                                            ? obj.subtitle.substring(0, 3)
                                            : obj.subtitle
                                        }
                                    </Box>
                                )}
                            </Box>
                        </th>
                    )
                }
                return null
            })}
        </tr>
    )
}

export default Header;